"use client";

import React, { useState, useEffect, useRef } from "react";
import { ArrowUpRight, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const categories = ["All", "Web", "Branding", "Content"];
const projects = [{
  id: 1,
  title: "Luxe Brands E-Commerce",
  category: "Web",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575318/img/portfolio_luxe_web.png",
  client: "Luxe Brands",
  year: "2023",
  role: "Design & Development",
  description: "A high-end e-commerce experience designed to maximize conversions while maintaining a premium brand aesthetic. Features include seamless checkout, dynamic product filtering, and immersive lifestyle imagery integration.",
  link: "#"
}, {
  id: 2,
  title: "Rise Athletics Identity",
  category: "Branding",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575318/img/portfolio_rise_brand.png",
  client: "Rise Athletics",
  year: "2024",
  role: "Brand Strategy & Identity",
  description: "Complete brand overhaul for a rising athletic apparel company. We developed a bold, minimalist visual identity that resonates with high-performance athletes and fitness enthusiasts.",
  link: "#"
}, {
  id: 3,
  title: "Viral Growth Campaign",
  category: "Content",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575318/img/portfolio_social_content.png",
  client: "Various Creators",
  year: "2023",
  role: "Content Creation & Strategy",
  description: "A series of highly engaging short-form video campaigns that resulted in a 300% increase in organic reach and follower growth across Instagram and TikTok for our creator partners.",
  link: "#"
}, {
  id: 4,
  title: "Studio Minimal",
  category: "Web",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575318/img/portfolio_minimal_web.png",
  client: "Creative Studio",
  year: "2024",
  role: "Web Development",
  description: "A clean, fast-loading portfolio website built for a boutique creative agency. The focus was on smooth scrolling animations, large typography, and a distraction-free showcase of their work.",
  link: "#"
}, {
  id: 5,
  title: "Aura Skincare",
  category: "Branding",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575318/img/portfolio_aura_brand.png",
  client: "Aura Cosmetics",
  year: "2023",
  role: "Packaging & Branding",
  description: "Elegant and sustainable packaging design coupled with a refined brand identity for a new luxury skincare line targeting the modern, eco-conscious consumer.",
  link: "#"
}, {
  id: 6,
  title: "Athlete Spotlight Series",
  category: "Content",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575318/img/portfolio_athlete_content.png",
  client: "Pro Sports Agency",
  year: "2024",
  role: "Video Production",
  description: "A cinematic documentary-style video series highlighting the off-field lives and personal brands of top-tier athletes, designed for YouTube and social media distribution.",
  link: "#"
}];
function ProjectCard({
  project,
  index
}: {
  project: typeof projects[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <Dialog>
      <DialogTrigger asChild>
        <div ref={ref} data-index={index} className={`group flex flex-col gap-4 cursor-pointer transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
        transitionDelay: `${index * 100}ms`
      }}>
          <div className="relative overflow-hidden rounded-xl aspect-[4/3] bg-muted">
            <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
            {}
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
              <div className="bg-white text-black px-6 py-3 rounded-full text-sm font-medium tracking-wide transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 flex items-center gap-2 shadow-xl">
                View Project <ArrowUpRight className="w-4 h-4" />
              </div>
            </div>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-semibold tracking-tight text-foreground">
                {project.title}
              </h3>
              <p className="text-muted-foreground text-sm mt-1">{project.category}</p>
            </div>
          </div>
        </div>
      </DialogTrigger>

      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 overflow-hidden bg-background border-border rounded-xl flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {}
          <div className="relative w-full h-[50vh] md:h-[60vh] bg-muted">
            <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full p-6 md:p-12 flex flex-col justify-end">
              <DialogHeader className="text-left">
                <DialogTitle className="text-4xl md:text-6xl font-bold tracking-tighter text-foreground mb-2">
                  {project.title}
                </DialogTitle>
                <DialogDescription className="text-lg text-muted-foreground">
                  {project.client} &mdash; {project.year}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>

          {}
          <div className="p-6 md:p-12 grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="md:col-span-2 space-y-6">
              <h4 className="text-2xl font-semibold tracking-tight">The Challenge & Solution</h4>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {project.description}
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                By focusing on a cinematic, minimalist approach, we were able to elevate the brand's digital presence, ensuring it stands out in a crowded market while maintaining a sense of quiet luxury and authority.
              </p>
            </div>
            <div className="space-y-8">
              <div>
                <h5 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">Role</h5>
                <p className="text-foreground font-medium">{project.role}</p>
              </div>
              <div>
                <h5 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">Category</h5>
                <p className="text-foreground font-medium">{project.category}</p>
              </div>
              <div className="pt-4">
                <Button asChild className="w-full group">
                  <Link to={project.link} newTab>
                    Visit Live Site
                    <ArrowUpRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}
export default function ThePortfolioShowcase() {
  const [activeFilter, setActiveFilter] = useState("All");
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  const filteredProjects = projects.filter(project => activeFilter === "All" || project.category === activeFilter);
  return;
}